import jwtDecode from "jwt-decode";
import axios from "axios";
import { toast } from "react-toastify";

import config from "../../config";
import { resetAuth } from "../../redux/auth/actions";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { refreshToken } from "./auth";
import { useNavigate } from "react-router-dom";
import { clearUser, track } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { AUTH_LIMIT, AuthLimitError, authLimitFrom } from "./authLimit";

const AUTH_SESSION_KEY = "concierge_user";
export const getUserFromCookie = () => {
  const user = sessionStorage.getItem(AUTH_SESSION_KEY);
  return user ? (typeof user == "object" ? user : JSON.parse(user)) : null;
};

// An expired session ends the session with a full document load, which destroys
// every React effect — so the analytics side of it has to happen here, before the
// navigation, or it never happens at all (ANALYTICS.md B3). Order matters: emit the
// event while the user dimensions are still set, then clear them. All four 401 exits
// funnel through here so none of them can forget.
let redirecting = false;
const redirectToLogin = () => {
  // Drop the dead session first, every time: left in storage, the reloaded sign-in
  // page still looked signed-in, so background calls (the permission refresh) got
  // 401 again and sent the page back to sign-in, over and over.
  sessionStorage.removeItem(AUTH_SESSION_KEY);
  delete axios.defaults.headers.common["Authorization"];
  if (window.location.pathname.startsWith("/auth/")) return;
  // Several requests can fail together; one navigation (and one logout event) is enough.
  if (redirecting) return;
  redirecting = true;
  try {
    track(EVENTS.LOGOUT, { reason: "session_expired" });
    clearUser();
  } catch {
    // Analytics must never be able to break the auth redirect.
  }
  window.location.href = "/auth/login";
};

/** Marks a request whose 401 must not trigger a token refresh (the refresh call itself). */
const SKIP_AUTH_REFRESH = "skipAuthRefresh";

// One refresh at a time: requests that meet a 401 together share it.
let refreshInFlight: Promise<string | null> | null = null;
// After a refused refresh, don't ask again for a while.
const REFRESH_COOLDOWN_MS = 30_000;
let refreshRefusedAt = 0;

/** Renews the access token. Resolves the new token, or null when the server refused. */
const refreshSession = (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;
  if (Date.now() - refreshRefusedAt < REFRESH_COOLDOWN_MS) return Promise.resolve(null);
  refreshInFlight = (async () => {
    const user = getUserFromCookie();
    if (!user?.refresh_token) return null;
    try {
      const refresh = await axios.post(
        "/api/auth/refresh-token",
        { refresh_token: user.refresh_token },
        { [SKIP_AUTH_REFRESH]: true, [AUTH_LIMIT]: true } as any,
      );
      const data = refresh?.data?.data;
      if (!refresh?.data?.status || !data?.access_token) {
        refreshRefusedAt = Date.now();
        return null;
      }
      user.refresh_token = data.refresh_token;
      user.access_token = data.access_token;
      sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(user));
      axios.defaults.headers.common["Authorization"] = "Bearer " + data.access_token;
      refreshRefusedAt = 0;
      return data.access_token as string;
    } catch (e) {
      // Rate limited (429) or the limiter is down (503): the session is still good,
      // so the caller keeps it, and no cooldown starts. Every other failure ends it.
      if (e instanceof AuthLimitError) throw e;
      refreshRefusedAt = Date.now();
      return null;
    }
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

// Every path below that hands a message back to the UI reports it once. The HTTP
// status ONLY: an API error message can quote a field value, a customer name or a
// document reference, so the text itself never travels (ANALYTICS.md §9). A 401 is
// not reported here — it is a session expiry, already covered by the `logout` event
// above, and it never reaches a component as an error.
const trackApiError = (code: string | number) => {
  try {
    track(EVENTS.ERROR_SHOWN, { error_scope: "api", error_code: code });
  } catch {
    // Analytics must never be able to change how an API error surfaces.
  }
};

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;

    // Sign-in pages and the refresh call opt in to the rate-limit details (authLimit.ts).
    if (error?.config?.[AUTH_LIMIT]) {
      const limited = authLimitFrom(error);
      if (limited) {
        trackApiError(limited.status);
        return Promise.reject(limited);
      }
    }

    if (error && error.response && error.response.status === 404) {
      trackApiError(404);
      const msg = error.response?.data?.message || error.response?.data?.data || 'Not found';
      return Promise.reject(msg);
    } else if (error && error.response && error.response.status === 401) {
      const originalRequest = error.config || {};
      // The refresh call's own 401 is the answer to the refresh, not another
      // expired request. Running this handler for it started a new refresh for
      // every refused one: a storm of hundreds of refresh calls per second.
      if (originalRequest[SKIP_AUTH_REFRESH]) {
        return Promise.reject(error);
      }
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        let accessToken: string | null;
        try {
          accessToken = await refreshSession();
        } catch (e) {
          // Rate limited (429) or the limiter is down (503): the session is still good,
          // so keep it and let the caller show the message. Only a refused refresh ends it.
          if (e instanceof AuthLimitError) return Promise.reject(e.message);
          accessToken = null;
        }
        if (accessToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          return axios.request(originalRequest);
        }
      }
      redirectToLogin();
      return Promise.reject("Unauthorized");
    } else if (error.response) {
      switch (error.response.status) {
        case 403:
          message = "Access Forbidden";
          break;
        case 404:
          message = "Sorry! the data you are looking for could not be found";
          break;
        // A 422 from ValidationMiddleware carries the useful field-level detail in
        // `data` and the literal "validation fail" in `message` (ErrorResponse's
        // constructor is (data, message) and the middleware passes them in that
        // order), so for this status only, the detail wins over the generic tag.
        case 422: {
          const detail = error.response?.data?.data;
          message =
            (typeof detail === "string" && detail)
              || error.response?.data?.message
              || error.message
              || error;
          break;
        }
        default: {
          message =
            error.response?.data?.message
              || error.response?.data?.data
              || error.message
              || error;
        }
      }
      // Global error toast for page loads (GET). Mutations keep their own contextual
      // toasts, so we only surface fetch errors here to avoid double-toasting.
      if (error.config?.method === "get" && typeof message === "string") {
        toast.error(message, { toastId: message });
      }
      trackApiError(error.response.status);
      return Promise.reject(message);
    } else {
      trackApiError("network");
      const netMsg = error.message || "Network error";
      if (error.config?.method === "get") toast.error(netMsg, { toastId: netMsg });
      return Promise.reject(netMsg);
    }
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
  if (token) axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  else delete axios.defaults.headers.common["Authorization"];
};

class APICore {
  // Ensure URL is absolute (starts with /) to avoid relative-path issues
  private normalizeUrl = (url: string) => url.startsWith('/') || url.startsWith('http') ? url : `/${url}`;

  /**
   * Fetches data from given url
   */
  get = (url: string, params: any, cancelToken?: any) => {
    const normalizedUrl = this.normalizeUrl(url);
    if (params) {
      // Serialize the query string ONCE, dropping empty/undefined/null values. Previously
      // every key was appended (so `undefined` became the literal string "undefined") AND
      // axios re-serialized `params`, duplicating each key (arriving server-side as arrays).
      const queryString = Object.keys(params)
        .filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== "")
        .map((key) => `${key}=${encodeURIComponent(params[key])}`)
        .join("&");
      return axios.get(queryString ? `${normalizedUrl}?${queryString}` : normalizedUrl, { cancelToken });
    }
    return axios.get(normalizedUrl, { cancelToken });
  };

  getFile = (url: string, params: any) => {
    const normalizedUrl = this.normalizeUrl(url);
    let response;
    if (params) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.get(`${normalizedUrl}?${queryString}`, { responseType: "blob" });
    } else {
      response = axios.get(`${normalizedUrl}`, { responseType: "blob" });
    }
    return response;
  };

  getMultiple = (urls: string, params: any) => {
    const reqs = [];
    let queryString = "";
    if (params) {
      queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
    }

    for (const url of urls) {
      reqs.push(axios.get(`${this.normalizeUrl(url)}?${queryString}`));
    }
    return axios.all(reqs);
  };

  /**
   * post given data to url
   *
   * FormData needs the multipart content type, and it cannot come from the
   * global default set at the top of this file: that default is
   * `application/json`, and axios 1.x reads it BEFORE it looks at the body —
   * `transformRequest` (axios/lib/defaults/index.js:56) does
   * `hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data`. So a
   * FormData carrying a File was serialised to `{"upfile":{}}` (a File has no
   * enumerable own properties) and sent as JSON. express-fileupload then saw a
   * non-multipart body, `req.files` was undefined, and the server answered
   * 400 "No file uploaded" — which is how the admin service photo and the
   * supplier logo both failed to save.
   *
   * Callers that already pass their own config are untouched, so this can only
   * change requests that are broken today.
   */
  create = (url: string, data: any, config?:any) => {
    const cfg =
      typeof FormData !== "undefined" && data instanceof FormData && !config
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : config;
    return axios.post(this.normalizeUrl(url), data, cfg);
  };

  /**
   * Updates patch data
   */
  updatePatch = (url: string, data: any) => {
    return axios.patch(this.normalizeUrl(url), data);
  };

  /**
   * Updates data
   */
  update = (url: string, data: any, config?: any) => {
    return axios.put(this.normalizeUrl(url), data, config);
  };

  /**
   * Deletes data
   */
  delete = (url: string, data?: any) => {
    // A body is optional; endpoints that audit a removal (e.g. unlinking a
    // customer's exclusive partner) take their reason in it.
    return axios.delete(this.normalizeUrl(url), data === undefined ? undefined : { data });
  };

  /**
   * post given data to url with file
   */
  createWithFile = (url: string, data: any) => {
    // If data is already a FormData instance, use it directly
    const formData = data instanceof FormData ? data : (() => {
      const fd = new FormData();
      for (const k in data) {
        fd.append(k, data[k]);
      }
      return fd;
    })();

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, formData, config);
  };

  /**
   * post given data to url with file
   */
  updateWithFile = (url: string, data: any) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    return axios.patch(url, formData, config);
  };

  isUserAuthenticated = () => {
    const user = this.getLoggedInUser();
    if (!user || !user.access_token) {
      return false;
    }
    try {
      const decoded: { exp?: number } = jwtDecode(user.access_token);
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        return false;
      }
    } catch {
      return false;
    }
    setAuthorization(user.access_token);
    return true;
  };

  setLoggedInUser = (session: any) => {
    if (session)
      sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    else {
      sessionStorage.removeItem(AUTH_SESSION_KEY);
    }
  };
  /**
   * Returns the logged in user
   */
  getLoggedInUser = () => {
    return getUserFromCookie();
  };

  setUserInSession = (modifiedUser: any) => {
    let userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
    if (userInfo) {
      const { access_token, refresh_token, user } = JSON.parse(userInfo);
      this.setLoggedInUser({
        access_token,
        refresh_token,
        ...user,
        ...modifiedUser,
      });
    }
  };
}

/*
Check if token available in session
*/
let user = getUserFromCookie();
if (user) {
  const { token } = user;
  if (token) {
    setAuthorization(token);
  }
}

export { APICore, setAuthorization };
